import { MapPin, Clock, DollarSign, Users, Award, Zap, Globe, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEO/SEOHead";
import { JobPostingSchema } from "@/components/Schema/JobPostingSchema";
import { scrollToSection } from "@/utils/scrollUtils";
import { useNavigate } from "react-router-dom";

const Careers = () => {
  const navigate = useNavigate();
  const positions = [
    {
      title: "Senior AI Engineer",
      department: "Engineering",
      location: "Remote (Global)",
      type: "Full-time",
      salary: "$150,000 - $200,000",
      description: "Lead the development of cutting-edge AI solutions and machine learning models for enterprise clients.",
      requirements: [
        "5+ years in AI/ML development",
        "Experience with Python, TensorFlow, PyTorch",
        "Strong background in NLP and computer vision",
        "Experience with cloud platforms (AWS, Azure, GCP)"
      ]
    },
    {
      title: "NetSuite Developer",
      department: "Engineering",
      location: "Remote (Global)",
      type: "Full-time", 
      salary: "$120,000 - $160,000",
      description: "Design and implement enterprise NetSuite solutions with custom SuiteScript development.",
      requirements: [
        "3+ years NetSuite development experience",
        "Proficiency in SuiteScript 2.0",
        "Experience with SuiteCloud platform",
        "Strong understanding of ERP processes"
      ]
    },
    {
      title: "Shopify Plus Architect",
      department: "Engineering",
      location: "Remote (Global)",
      type: "Full-time",
      salary: "$110,000 - $150,000", 
      description: "Architect and build scalable e-commerce solutions on Shopify Plus platform.",
      requirements: [
        "4+ years Shopify development",
        "Shopify Plus certification preferred",
        "Experience with React, Node.js",
        "Knowledge of e-commerce best practices"
      ]
    },
    {
      title: "Solutions Architect",
      department: "Engineering",
      location: "Remote (Global)",
      type: "Full-time",
      salary: "$140,000 - $180,000",
      description: "Design enterprise-level integration solutions using Celigo and other iPaaS platforms.",
      requirements: [
        "5+ years in enterprise integrations",
        "Experience with Celigo platform",
        "Strong API development skills",
        "Cloud architecture experience"
      ]
    },
    {
      title: "Business Development Manager",
      department: "Sales",
      location: "Remote (US/Europe)",
      type: "Full-time",
      salary: "$90,000 - $120,000 + Commission",
      description: "Drive new business acquisition and expand our client portfolio globally.",
      requirements: [
        "3+ years B2B sales experience",
        "Technology/SaaS sales background",
        "Strong communication skills",
        "Track record of exceeding targets"
      ]
    },
    {
      title: "DevOps Engineer",
      department: "Engineering", 
      location: "Remote (Global)",
      type: "Full-time",
      salary: "$100,000 - $140,000",
      description: "Build and maintain CI/CD pipelines and cloud infrastructure for our enterprise solutions.",
      requirements: [
        "3+ years DevOps experience",
        "Experience with Docker, Kubernetes",
        "Cloud platforms (AWS, Azure)",
        "Infrastructure as Code (Terraform)"
      ]
    }
  ];

  const benefits = [
    {
      icon: Globe,
      title: "100% Remote",
      description: "Work from anywhere in the world with flexible hours"
    },
    {
      icon: DollarSign,
      title: "Competitive Pay",
      description: "Top-tier salaries with performance bonuses and equity"
    },
    {
      icon: Award,
      title: "Growth Opportunities", 
      description: "Continuous learning with certification and conference budgets"
    },
    {
      icon: Users,
      title: "Amazing Team",
      description: "Work with experienced engineers and industry professionals"
    },
    {
      icon: Zap,
      title: "Cutting-Edge Tech",
      description: "Work on the latest AI and enterprise technologies"
    },
    {
      icon: Coffee,
      title: "Work-Life Balance",
      description: "Unlimited PTO, mental health support, and wellness programs"
    }
  ];

  const perks = [
    "🏠 $2,000 home office setup budget",
    "💻 Top-tier MacBook Pro or custom PC",
    "🎓 $5,000 annual learning & development budget",
    "🏥 Premium health insurance (100% covered)",
    "🦷 Dental & vision coverage",
    "🏖️ Unlimited paid time off",
    "🎉 Annual team retreats in exotic locations",
    "💰 Performance bonuses up to 25% of salary",
    "📈 Equity participation program",
    "🍔 Monthly food & wellness allowance"
  ];

  return (
    <>
      <SEOHead
        title="Careers at Fortify | Join Our Team of AI & Tech Experts"
        description="Join the Fortify team. We're hiring AI engineers, NetSuite developers, and Shopify experts. Remote-first culture, competitive salaries, and cutting-edge projects."
        canonicalUrl="https://www.fortifytek.com/careers"
        keywords={["careers", "jobs", "hiring", "AI jobs", "NetSuite developer jobs", "Shopify developer jobs", "remote work", "tech careers"]}
      />
      {positions.map((pos, index) => {
        const salaryParts = pos.salary.replace(/[$,]/g, '').split('-').map(s => parseInt(s.trim()));
        return (
          <JobPostingSchema
            key={index}
            title={pos.title}
            description={pos.description}
            datePosted="2024-01-01"
            hiringOrganization={{
              name: "Fortify",
              sameAs: "https://www.fortifytek.com",
              logo: "https://www.fortifytek.com/logo.png"
            }}
            jobLocation={{
              addressCountry: "US",
              addressLocality: "Remote"
            }}
            baseSalary={{
              currency: "USD",
              value: {
                minValue: salaryParts[0],
                maxValue: salaryParts[1],
                unitText: "YEAR"
              }
            }}
          />
        );
      })}
      <div className="min-h-screen bg-background">
        <Navigation />
        
        {/* Hero Section */}
      <section className="pt-40 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-8 leading-tight animate-fade-in">
              <span className="block mb-2">
                Build the Future
              </span>
              <span className="block text-amber-400 pb-2">
                With FortifyTek
              </span>
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              Join our team of innovators building cutting-edge enterprise solutions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in delay-500">
              <Button 
                size="lg" 
                className="bg-white text-black hover:bg-zinc-200 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105"
                onClick={() => scrollToSection('positions')}
              >
                View Open Positions
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-zinc-700 text-white hover:bg-zinc-800 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105"
                onClick={() => navigate('/contact')}
              >
                Send Us Your Resume
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Why Join </span>
              <span className="text-amber-400">FortifyTek?</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              We offer more than just a job - we provide a platform for growth, innovation, and making a real impact.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 hover:border-amber-500/50 transition-all duration-500 hover:scale-105 group animate-fade-in"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="flex items-center justify-center w-16 h-16 bg-amber-500/10 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                  <benefit.icon className="w-8 h-8 text-amber-500" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-amber-400 transition-colors">
                  {benefit.title}
                </h3>
                
                <p className="text-white/70 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>

          {/* Perks Grid */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Amazing Perks & Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {perks.map((perk, index) => (
                <div key={index} className="text-white/80 text-lg">
                  {perk}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="positions" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Open </span>
              <span className="text-amber-400">Positions</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Join our growing team and help us build the next generation of enterprise solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {positions.map((position, index) => (
              <div
                key={index}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 hover:border-amber-500/50 transition-all duration-500 hover:scale-105 group animate-fade-in"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                      {position.title}
                    </h3>
                    <div className="text-amber-400/80 font-medium">{position.department}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-green-400 font-bold text-lg">{position.salary}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 mb-6 text-white/60">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{position.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{position.type}</span>
                  </div>
                </div>
                
                <p className="text-white/70 mb-6 leading-relaxed">
                  {position.description}
                </p>
                
                <div className="mb-8">
                  <h4 className="text-white font-semibold mb-3">Key Requirements:</h4>
                  <ul className="space-y-2">
                    {position.requirements.map((req, reqIndex) => (
                      <li key={reqIndex} className="flex items-center text-white/70">
                        <div className="w-2 h-2 bg-amber-500 rounded-full mr-3"></div>
                        <span className="text-sm">{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Button 
                  className="w-full bg-white text-black hover:bg-zinc-200 font-medium transition-all duration-300 hover:scale-105"
                  onClick={() => navigate('/contact')}
                >
                  Apply Now
                </Button>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-white mb-4">Don't See Your Role?</h3>
              <p className="text-white/70 mb-6">
                We're always looking for talented individuals. Send us your resume and tell us how you'd like to contribute!
              </p>
              <Button 
                size="lg"
                className="bg-white text-black hover:bg-zinc-200 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105"
                onClick={() => navigate('/contact')}
              >
                Send Your Resume
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
    </>
  );
};

export default Careers;